/**
 * 개별 알림 아이템 컴포넌트
 */
import { Badge } from '@/components/ui/badge';
import { NotificationIcon, TimeIcon, CloseIcon } from './Icons';
import type { Notification, NotificationType } from './types';
import type { NotificationTranslations } from './constants';

interface NotificationItemProps {
  notification: Notification;
  t: NotificationTranslations;
  formatTimeAgo: (timestamp: Date) => string;
  onMarkAsRead: (notificationId: string) => void;
  onDeleteNotification: (notificationId: string) => void;
}

/**
 * 알림 타입에 따른 텍스트 반환
 */
function getTypeLabel(type: NotificationType, t: NotificationTranslations): string {
  switch (type) {
    case 'order':
      return t.orderReady;
    case 'promotion':
      return t.promotion;
    case 'system':
      return t.systemAlert;
    default:
      return '';
  }
}

/**
 * Badge 스타일 반환
 */
function getBadgeClassName(type: NotificationType): string {
  const baseClass = 'text-xs h-5 px-2 border-current';
  switch (type) {
    case 'order':
      return `${baseClass} text-primary border-primary`;
    case 'promotion':
      return `${baseClass} text-secondary border-secondary`;
    case 'system':
      return `${baseClass} text-warning border-warning`;
    default:
      return `${baseClass} text-muted-foreground border-muted-foreground`;
  }
}

export function NotificationItem({
  notification,
  t,
  formatTimeAgo,
  onMarkAsRead,
  onDeleteNotification,
}: NotificationItemProps) {
  const handleDeleteClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onDeleteNotification(notification.id);
  };

  const handleDeleteKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  return (
    <div
      className={`relative p-3 mb-2 rounded-lg border transition-all duration-200 hover:shadow-md ${
        notification.isRead
          ? 'bg-muted border-border'
          : 'bg-background border-primary/30 shadow-sm'
      }`}
      onClick={() => onMarkAsRead(notification.id)}
      role={notification.isRead ? undefined : 'button'}
      tabIndex={notification.isRead ? undefined : 0}
      onKeyDown={(e) => {
        if (!notification.isRead && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          e.stopPropagation();
          onMarkAsRead(notification.id);
        }
      }}
    >
      {/* 읽지 않음 표시 */}
      {!notification.isRead && (
        <div className="absolute top-3 right-3 w-2 h-2 bg-primary rounded-full" />
      )}

      <div className="flex items-start gap-3">
        <div className="mt-1">
          <NotificationIcon type={notification.type} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <p className={`text-sm font-medium ${
              notification.isRead
                ? 'text-muted-foreground'
                : 'text-foreground'
            }`}>
              {notification.title}
            </p>
            <Badge
              variant="outline"
              className={getBadgeClassName(notification.type)}
            >
              {getTypeLabel(notification.type, t)}
            </Badge>
          </div>

          <p className={`text-sm mb-2 ${
            notification.isRead
              ? 'text-foreground-muted'
              : 'text-foreground'
          }`}>
            {notification.message}
          </p>

          {notification.restaurantName && (
            <p className="text-xs text-muted-foreground mb-1">
              {notification.restaurantName}
            </p>
          )}

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1 text-xs text-foreground-muted">
              <TimeIcon />
              {formatTimeAgo(notification.timestamp)}
            </div>

            <button
              onClick={handleDeleteClick}
              onKeyDown={handleDeleteKeyDown}
              className="text-xs text-muted-foreground hover:text-warning transition-colors p-1"
              aria-label="Delete notification"
            >
              <CloseIcon className="h-3 w-3" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
